import { useState } from 'react';
import Modal from '../common/Modal';
import Button from '../common/Button';
import Input from '../common/Input';
import { api } from '../../api/client';

interface InvoiceEmailModalProps {
  constructionId: string;
  invoiceNumber: string;
  companyName?: string;
  companyEmail?: string;
  totalAmount: number;
  pdfBase64: string;
  onClose: () => void;
  onSuccess: () => void;
}

export default function InvoiceEmailModal({
  constructionId,
  invoiceNumber,
  companyName = '',
  companyEmail = '',
  totalAmount,
  pdfBase64,
  onClose,
  onSuccess,
}: InvoiceEmailModalProps) {
  const [to, setTo] = useState(companyEmail);
  const [subject, setSubject] = useState(`【請求書送付】${invoiceNumber} - ${companyName}`);
  const [message, setMessage] = useState(
    `平素より大変お世話になっております。\n\n下記の通り請求書を送付いたしますので、ご査収くださいますようお願い申し上げます。\n\nご不明な点がございましたら、お気軽にお問い合わせください。\n\n何卒よろしくお願い申し上げます。`
  );
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('ja-JP', { style: 'currency', currency: 'JPY' }).format(amount);
  };

  const handleSend = async () => {
    if (sending) return;
    if (!to) {
      setError('送信先メールアドレスを入力してください');
      return;
    }

    // メールアドレスの簡易バリデーション
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(to)) {
      setError('有効なメールアドレスを入力してください');
      return;
    }

    setSending(true);
    setError(null);

    try {
      await api.post(`/api/constructions/${constructionId}/send-invoice`, {
        to,
        subject,
        message,
        pdfBase64,
        invoiceNumber,
        invoiceAmount: totalAmount,
      });
      onSuccess();
    } catch (err) {
      console.error('メール送信エラー:', err);
      setError('メールの送信に失敗しました。もう一度お試しください。');
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} title="請求書メール送信" size="wide">
      <div className="space-y-4">
        {/* 送信情報サマリー */}
        <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <span className="font-medium text-blue-900 dark:text-blue-100">送信する請求書</span>
          </div>
          <div className="text-sm text-blue-800 dark:text-blue-200 space-y-1">
            <p>請求書番号: {invoiceNumber}</p>
            <p>請求先: {companyName || '(未設定)'}</p>
            <p>請求金額: <span className="font-bold">{formatCurrency(totalAmount)}</span></p>
          </div>
        </div>

        {/* エラーメッセージ */}
        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 p-3 rounded-lg text-sm">
            {error}
          </div>
        )}

        {/* 送信先 */}
        <Input
          label="送信先メールアドレス"
          type="email"
          value={to}
          onChange={(e) => setTo(e.target.value)}
          placeholder="example@company.co.jp"
          required
        />

        {/* 件名 */}
        <Input
          label="件名"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          placeholder="請求書送付のご案内"
          required
        />

        {/* メッセージ本文 */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            メッセージ本文
          </label>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={6}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="メッセージを入力..."
          />
        </div>

        {/* 注意事項 */}
        <div className="bg-gray-50 dark:bg-gray-800 p-3 rounded-lg text-sm text-gray-600 dark:text-gray-400">
          <div className="flex items-start gap-2">
            <svg className="w-4 h-4 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p>請求書PDFがメールに添付されます。送信後、請求ステータスが「請求済」に更新されます。</p>
          </div>
        </div>

        {/* ボタン */}
        <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
          <Button variant="secondary" onClick={onClose} disabled={sending}>
            キャンセル
          </Button>
          <Button onClick={handleSend} loading={sending}>
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
            </svg>
            送信
          </Button>
        </div>
      </div>
    </Modal>
  );
}
